import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError, fileDownloadUrl } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UploadButton from "@/components/UploadButton";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ChevronUp, Plus, X, Trash2, Pencil, Archive, RotateCcw, FileText, History, ClipboardList, FileDown, Sheet, ScrollText } from "lucide-react";
import { toast } from "sonner";

const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const downloadFile = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
};
const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Date/heure", "Code", "Signification", "Employe", "Note"];
  return [head.map(esc).join(","), ...rows.map((r) => [fmtDT(r.entry_at), r.code, r.meaning, r.employee_name, r.note].map(esc).join(","))].join("\n");
};

function GridDialog({ open, onOpenChange, residentId, grid, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", description: "", codes: [{ code: "", meaning: "" }] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(grid ? { name: grid.name, description: grid.description || "", codes: grid.codes?.length ? grid.codes.map((c) => ({ ...c })) : [{ code: "", meaning: "" }] }
      : { name: "", description: "", codes: [{ code: "", meaning: "" }] });
  }, [open, grid]);
  const setCode = (i, k, v) => setForm((f) => { const codes = [...f.codes]; codes[i] = { ...codes[i], [k]: v }; return { ...f, codes }; });
  const save = async () => {
    const codes = form.codes.filter((c) => c.code.trim() && c.meaning.trim());
    if (!form.name.trim() || codes.length === 0) { toast.error(t("gridNeedsCodes")); return; }
    setSaving(true);
    try {
      if (grid) await api.put(`/grids/${grid.id}`, { name: form.name, description: form.description, codes });
      else await api.post("/grids", { resident_id: residentId, name: form.name, description: form.description, codes });
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{grid ? t("editGrid") : t("addGrid")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">{t("gridName")}</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="grid-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("description")}</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="grid-description" /></div>
          <div className="space-y-2">
            <Label className="text-xs">{t("legendCodes")}</Label>
            {form.codes.map((c, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input className="w-20" placeholder={t("code")} value={c.code} onChange={(e) => setCode(i, "code", e.target.value)} data-testid="grid-code-input" />
                <Input className="flex-1" placeholder={t("meaning")} value={c.meaning} onChange={(e) => setCode(i, "meaning", e.target.value)} data-testid="grid-meaning-input" />
                <Button variant="ghost" size="icon" onClick={() => setForm((f) => ({ ...f, codes: f.codes.filter((_, j) => j !== i) }))}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, codes: [...f.codes, { code: "", meaning: "" }] }))} data-testid="add-code-row"><Plus className="w-4 h-4 mr-1" />{t("addCode")}</Button>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button><Button onClick={save} disabled={saving} data-testid="save-grid">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntryDialog({ open, onOpenChange, grid, isManager, onSaved }) {
  const { t } = useT();
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [att, setAtt] = useState(null);
  const [when, setWhen] = useState(nowLocal());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setCode(""); setNote(""); setAtt(null); setWhen(nowLocal()); } }, [open]);
  const save = async () => {
    if (!code) { toast.error(t("selectCode")); return; }
    setSaving(true);
    try {
      const body = { code, note, attachment: att };
      if (isManager) body.entry_at = new Date(when).toISOString();
      await api.post(`/grids/${grid.id}/entries`, body);
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("addEntry")} · {grid?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">{t("code")}</Label>
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger data-testid="entry-code"><SelectValue placeholder={t("selectCode")} /></SelectTrigger>
              <SelectContent>{(grid?.codes || []).map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.meaning}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isManager && <div className="space-y-1.5"><Label className="text-xs">{t("dateTime")}</Label><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="entry-datetime" /></div>}
          <div className="space-y-1.5"><Label className="text-xs">{t("note")}</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="entry-note" /></div>
          <div className="flex items-center gap-2 flex-wrap">
            <UploadButton label={t("attachment")} accept="image/*,application/pdf" category="grid-entry" testId="entry-upload" onUploaded={(d) => setAtt({ file_id: d.id, name: d.original_filename, content_type: d.content_type })} />
            {att && <span className="text-xs flex items-center gap-1 bg-muted px-2 py-1 rounded"><FileText className="w-3 h-3" />{att.name}<button onClick={() => setAtt(null)}><X className="w-3 h-3" /></button></span>}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button><Button onClick={save} disabled={saving} data-testid="save-entry">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GridCard({ grid, residentName, isAdmin, isManager, employees, onChanged }) {
  const { t } = useT();
  const [showHist, setShowHist] = useState(false);
  const [entries, setEntries] = useState([]);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [fCode, setFCode] = useState("all");
  const [fEmp, setFEmp] = useState("all");
  const [fStart, setFStart] = useState("");
  const [fEnd, setFEnd] = useState("");

  const loadEntries = useCallback(async () => {
    const params = { grid_id: grid.id };
    if (fCode !== "all") params.code = fCode;
    if (fEmp !== "all") params.employee_id = fEmp;
    if (fStart) params.start = new Date(fStart).toISOString();
    if (fEnd) params.end = new Date(fEnd + "T23:59:59").toISOString();
    const { data } = await api.get("/grids/entries", { params });
    setEntries(data);
  }, [grid.id, fCode, fEmp, fStart, fEnd]);
  useEffect(() => { if (showHist) loadEntries(); }, [showHist, loadEntries]);

  const setStatus = async (status) => { try { await api.put(`/grids/${grid.id}`, { status }); toast.success(t("save")); onChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async () => { if (!window.confirm(t("deleteGridConfirm"))) return; try { await api.delete(`/grids/${grid.id}`); toast.success(t("delete")); onChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delEntry = async (id) => { try { await api.delete(`/grids/entries/${id}`); loadEntries(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const fetchForExport = async () => {
    const params = { grid_id: grid.id };
    if (fCode !== "all") params.code = fCode;
    if (fEmp !== "all") params.employee_id = fEmp;
    if (fStart) params.start = new Date(fStart).toISOString();
    if (fEnd) params.end = new Date(fEnd + "T23:59:59").toISOString();
    const { data } = await api.get("/grids/entries", { params });
    return data;
  };
  const exportCsv = async () => {
    const rows = await fetchForExport();
    downloadFile("\uFEFF" + toCsv(rows), `${grid.name}.csv`, "text/csv;charset=utf-8;");
  };
  const exportPdf = async () => {
    const rows = await fetchForExport();
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(grid.name, 14, 18);
    doc.setFontSize(10);
    doc.text(`Résident : ${residentName || ""}`, 14, 26);
    if (grid.description) doc.text(`Description : ${grid.description}`, 14, 32, { maxWidth: 182 });
    doc.text(`Légende : ${grid.codes.map((c) => `${c.code}=${c.meaning}`).join("  ·  ")}`, 14, 38, { maxWidth: 182 });
    if (fStart || fEnd) doc.text(`Période : ${fStart || "…"} → ${fEnd || "…"}`, 14, 44);
    autoTable(doc, {
      startY: fStart || fEnd ? 50 : 46,
      head: [["Date et heure", "Code", "Signification", "Employé", "Note"]],
      body: rows.map((r) => [fmtDT(r.entry_at), r.code, r.meaning, r.employee_name || "", r.note || ""]),
      styles: { fontSize: 9, cellPadding: 2 }, headStyles: { fillColor: [59, 130, 246] },
    });
    doc.save(`${grid.name}.pdf`);
  };

  return (
    <div className={`rounded-xl border bg-card ${grid.status === "archived" ? "border-dashed opacity-70" : "border-border"}`} data-testid="grid-card">
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold flex items-center gap-2">{grid.name}
              {grid.status === "archived" && <Badge variant="secondary" className="text-[10px]">{t("archived")}</Badge>}
            </h3>
            {grid.description && <p className="text-xs text-muted-foreground mt-0.5">{grid.description}</p>}
          </div>
          {isAdmin && (
            <div className="flex gap-0.5 shrink-0">
              <Button variant="ghost" size="icon" onClick={() => setEditOpen(true)} title={t("edit")} data-testid="edit-grid"><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setStatus(grid.status === "archived" ? "active" : "archived")} title={grid.status === "archived" ? t("activate") : t("archive")} data-testid="archive-grid">{grid.status === "archived" ? <RotateCcw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}</Button>
              <Button variant="ghost" size="icon" onClick={del} title={t("delete")} data-testid="delete-grid"><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          )}
        </div>
        {/* legend */}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {grid.codes.map((c) => <span key={c.code} className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary"><b>{c.code}</b> = {c.meaning}</span>)}
        </div>
        <div className="flex gap-2 mt-3">
          {grid.status !== "archived" && <Button size="sm" onClick={() => setEntryOpen(true)} data-testid="open-add-entry"><Plus className="w-3.5 h-3.5 mr-1" />{t("addEntry")}</Button>}
          <Button size="sm" variant="outline" onClick={() => setShowHist((s) => !s)} data-testid="toggle-history"><History className="w-3.5 h-3.5 mr-1" />{t("history")}<ChevronUp className={`w-3.5 h-3.5 ml-1 transition-transform ${showHist ? "" : "rotate-180"}`} /></Button>
          <Button size="sm" variant="ghost" onClick={exportPdf} title="PDF" data-testid="export-pdf"><FileDown className="w-3.5 h-3.5 mr-1" />PDF</Button>
          <Button size="sm" variant="ghost" onClick={exportCsv} title="CSV" data-testid="export-csv"><Sheet className="w-3.5 h-3.5 mr-1" />CSV</Button>
        </div>
      </div>

      {showHist && (
        <div className="border-t border-border p-4 space-y-3" data-testid="grid-history">
          <div className="flex flex-wrap gap-2">
            <Select value={fCode} onValueChange={setFCode}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-code"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("allCodes")}</SelectItem>{grid.codes.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={fEmp} onValueChange={setFEmp}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-employee"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("allEmployees")}</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" className="h-8 w-36 text-xs" value={fStart} onChange={(e) => setFStart(e.target.value)} data-testid="filter-start" />
            <Input type="date" className="h-8 w-36 text-xs" value={fEnd} onChange={(e) => setFEnd(e.target.value)} data-testid="filter-end" />
          </div>
          <div className="space-y-1.5 max-h-80 overflow-y-auto scrollbar-thin">
            {entries.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t("noEntries")}</p>}
            {entries.map((en) => (
              <div key={en.id} className="flex items-start gap-2 p-2 rounded-lg border border-border text-sm" data-testid="entry-row">
                <span className="w-8 h-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">{en.code}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{en.meaning}</p>
                  <p className="text-xs text-muted-foreground">{fmtDT(en.entry_at)} · {en.employee_name}</p>
                  {en.note && <p className="text-xs mt-0.5">{en.note}</p>}
                  {en.attachment && <a href={fileDownloadUrl(en.attachment.file_id)} target="_blank" rel="noreferrer" className="text-xs text-primary flex items-center gap-1 mt-0.5"><FileText className="w-3 h-3" />{en.attachment.name}</a>}
                </div>
                {isManager && <button onClick={() => delEntry(en.id)} className="text-muted-foreground hover:text-destructive" data-testid="delete-entry"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
          </div>
        </div>
      )}

      <EntryDialog open={entryOpen} onOpenChange={setEntryOpen} grid={grid} isManager={isManager} onSaved={() => { if (showHist) loadEntries(); }} />
      <GridDialog open={editOpen} onOpenChange={setEditOpen} grid={grid} onSaved={onChanged} />
    </div>
  );
}

export default function GridsPanel({ residentId, residentName }) {
  const { t } = useT();
  const { role, isSuperAdmin } = useCenter();
  const isAdmin = isSuperAdmin || role === "admin";
  const isManager = isAdmin || role === "supervisor";
  const [grids, setGrids] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = useCallback(() => { api.get("/grids", { params: { resident_id: residentId } }).then((r) => setGrids(r.data)).catch(() => {}); }, [residentId]);
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, [load]);
  const openAudit = async () => { try { const { data } = await api.get("/grids/audit", { params: { resident_id: residentId } }); setAudit(data); setAuditOpen(true); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="rsection-grids">
      <div className="flex items-center justify-between px-5 py-4">
        <h2 className="font-display text-lg font-bold text-primary flex items-center gap-2"><ClipboardList className="w-5 h-5" />{t("ratingGrids")}</h2>
        <div className="flex items-center gap-2">
          {isManager && <Button size="sm" variant="outline" onClick={openAudit} data-testid="audit-button"><ScrollText className="w-4 h-4 mr-1" />{t("auditLog")}</Button>}
          {isAdmin && <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="add-grid-button"><Plus className="w-4 h-4 mr-1" />{t("addGrid")}</Button>}
          <button onClick={() => setOpen((o) => !o)} className="w-7 h-7 rounded-full hover:bg-accent flex items-center justify-center"><ChevronUp className={`w-4 h-4 transition-transform ${open ? "" : "rotate-180"}`} /></button>
        </div>
      </div>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          {grids.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t("noGrids")}</p>}
          {grids.map((g) => <GridCard key={g.id} grid={g} residentName={residentName} isAdmin={isAdmin} isManager={isManager} employees={employees} onChanged={load} />)}
        </div>
      )}
      <GridDialog open={createOpen} onOpenChange={setCreateOpen} residentId={residentId} onSaved={load} />
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("auditLog")}</DialogTitle></DialogHeader>
          <div className="space-y-1.5 max-h-96 overflow-y-auto scrollbar-thin">
            {audit.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t("noData")}</p>}
            {audit.map((a) => (
              <div key={a.id} className="text-sm p-2 rounded-lg border border-border" data-testid="audit-row">
                <p><b className="capitalize">{t(a.action)}</b> · {a.entity === "grid" ? t("gridName") : t("addEntry")} · <span className="text-muted-foreground">{fmtDT(a.at)}</span></p>
                <p className="text-xs text-muted-foreground">{a.actor_name}
                  {a.entity === "entry" && a.after ? ` — ${a.after.code}=${a.after.meaning}` : ""}
                  {a.entity === "grid" && (a.after || a.before) ? ` — ${(a.after || a.before).name}` : ""}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
